import { useEffect, useState } from "react";
import { useTrans } from "../hooks";
import { Box, Divider, IconButton, VStack } from "@chakra-ui/react";
import {
    MapContainer,
    Marker,
    Popup,
    TileLayer,
    Tooltip as LeafletTooltip,
    useMapEvents,
    MapContainerProps,
    useMap,
} from "react-leaflet";
import L from "leaflet";
import { AppIcon } from "./AppIcon";
import { AppGeoSearch, MapLocation } from "./AppGeoSearch";
import { Company, Department, Partnership, Region, School } from "../apis";
import { APPC } from "../config";
import MarkerClusterGroup from "react-leaflet-cluster";
import "react-leaflet-cluster/dist/assets/MarkerCluster.css";
import "react-leaflet-cluster/dist/assets/MarkerCluster.Default.css";

const PARTNERSHIPSTATUS = APPC.BE.Partnership.STATUS;

const defaultMapContainerProps: MapContainerProps = {
    center: [48.8575475, 2.3513765],
    zoom: 15,
    minZoom: 12,
    maxZoom: 18,
    scrollWheelZoom: true,
    style: { height: "100%", width: "100%" },
};

const getMarkerByColor = (color: string) =>
    new L.Icon({
        iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
        shadowUrl:
            "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41],
    });

const getMarkerColorByStatus = (partnershipStatus: string) => {
    switch (partnershipStatus) {
        case PARTNERSHIPSTATUS.STATUS_APPROVED:
            return "green";

        case PARTNERSHIPSTATUS.STATUS_PENDING:
            return "yellow";

        case PARTNERSHIPSTATUS.STATUS_REJECTED:
            return "red";

        default:
            return "blue";
    }
};

export interface AppSchoolCompanyPartnershipMapProps {
    list: Company[];
    partnershipStatusList: Record<string, Partnership>;
    activeSchoolOrCompany: Company | School | null;
    onCenterChange: (lat: number, lng: number) => void;
    actionElement: (company: Company) => JSX.Element;
    mapProps?: MapContainerProps;
}

export const AppSchoolCompanyPartnershipMap = ({
    list,
    partnershipStatusList,
    activeSchoolOrCompany,
    onCenterChange,
    actionElement,
    mapProps,
}: AppSchoolCompanyPartnershipMapProps) => {
    const { t } = useTrans();

    const [latLng, setLatLng] = useState<[number, number]>([
        48.8575475, 2.3513765,
    ]);
    const center = mapProps?.center;

    const MapEvents = () => {
        useMapEvents({
            moveend: (e) => {
                const center = e.target.getCenter();
                onCenterChange(center.lat, center.lng);
            },
            zoomend: (e) => {
                const center = e.target.getCenter();
                onCenterChange(center.lat, center.lng);
            },
        });
        return null;
    };

    const ChangeView = ({ center }: { center: L.LatLngExpression }) => {
        const map = useMap();

        useEffect(() => {
            map.setView(center);
        }, [center, map]);

        return null;
    };

    useEffect(() => {
        if (
            activeSchoolOrCompany?.latitude &&
            activeSchoolOrCompany?.longitude
        ) {
            setLatLng([
                Number(activeSchoolOrCompany.latitude),
                Number(activeSchoolOrCompany.longitude),
            ]);
        }
    }, [activeSchoolOrCompany]);

    return (
        <Box w="full" h="full" position="relative">
            <MapContainer
                {...defaultMapContainerProps}
                {...mapProps}
                attributionControl={false}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {center && <ChangeView center={center} />}
                <MapEvents />
                <AppGeoSearch
                    onSelect={(location: MapLocation) => {
                        onCenterChange(location.lat, location.lng);
                    }}
                />
                {latLng && (
                    <Marker
                        position={[latLng[0], latLng[1]]}
                        icon={getMarkerByColor("black")}
                    >
                        <LeafletTooltip
                            direction="top"
                            offset={[0, -30]}
                            opacity={1}
                        >
                            {activeSchoolOrCompany?.name}
                        </LeafletTooltip>
                        <Popup autoPan={false}>
                            <Box textStyle={"boldSm"} mb={1}>
                                {activeSchoolOrCompany?.name}
                            </Box>
                            <Box>
                                {activeSchoolOrCompany?.address} -{" "}
                                {activeSchoolOrCompany?.city} -{" "}
                                {activeSchoolOrCompany?.zipCode}
                            </Box>
                        </Popup>
                    </Marker>
                )}
                <MarkerClusterGroup>
                    {list.map((company) => {
                        const lat = Number(company.latitude);
                        const lng = Number(company.longitude);
                        if (!lat || !lng) {
                            return null;
                        }
                        const partnershipStatus = Object.keys(
                            partnershipStatusList
                        ).includes(String(company?.id))
                            ? partnershipStatusList[company?.id]?.status
                            : "";
                        const markerColor =
                            getMarkerColorByStatus(partnershipStatus);

                        return (
                            <Marker
                                key={company.id}
                                position={[lat, lng]}
                                icon={getMarkerByColor(markerColor)}
                            >
                                {partnershipStatus && (
                                    <LeafletTooltip
                                        direction="top"
                                        offset={[0, -30]}
                                        opacity={1}
                                    >
                                        {t(
                                            `con.Partnership:STATUS_${
                                                partnershipStatus
                                            }`
                                        )}
                                    </LeafletTooltip>
                                )}
                                <Popup autoPan={false}>
                                    <Box textStyle={"boldSm"} mb={2}>
                                        {company.name}
                                    </Box>
                                    <Divider
                                        mb={2}
                                        borderColor={
                                            "var(--chakra-colors-primaryT80)"
                                        }
                                    />
                                    <VStack
                                        w="full"
                                        justifyContent="center"
                                        alignItems="flex-start"
                                        spacing={1}
                                    >
                                        <Box textStyle={"mediumSm"}>
                                            <strong>{`${t("ent.Company:region.label")}: `}</strong>
                                            {(company?.region as Region)?.name}
                                        </Box>
                                        <Box textStyle={"mediumSm"}>
                                            <strong>{`${t("ent.Company:department.label")}: `}</strong>
                                            {
                                                (
                                                    company?.department as Department
                                                )?.name
                                            }
                                        </Box>
                                        <Box textStyle={"mediumSm"}>
                                            <strong>{`${t("ent.Company:address.label")}: `}</strong>
                                            {company.address} - {company.city} -{" "}
                                            {company.zipCode}
                                        </Box>
                                        <Box mt={1.5}>
                                            {!partnershipStatus &&
                                                actionElement &&
                                                actionElement(company)}
                                        </Box>
                                    </VStack>
                                </Popup>
                            </Marker>
                        );
                    })}
                </MarkerClusterGroup>
            </MapContainer>
            <Box position="absolute" top="128px" left="11px">
                <IconButton
                    w={"32px"}
                    h={"32px"}
                    variant={"secondary"}
                    border={"1px solid var(--chakra-colors-greyT60)"}
                    backgroundColor={"var(--chakra-colors-white)"}
                    boxShadow="xl"
                    aria-label="back to location"
                    icon={
                        <AppIcon
                            name="ics-location-gps-fixed"
                            w="1.1rem"
                            color="var(--chakra-colors-black)"
                        />
                    }
                    onClick={() => {
                        onCenterChange(latLng[0], latLng[1]);
                    }}
                />
            </Box>
        </Box>
    );
};
